import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiShield, FiAlertTriangle, FiUpload, FiX, FiCheck, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { useWallet } from '../context/WalletContext';
import '../styles/ImportWalletPage.css';

const ImportWalletPage = () => {
  const [mnemonic, setMnemonic] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPhrase, setShowPhrase] = useState(false);
  const [validWords, setValidWords] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [importStep, setImportStep] = useState('');
  const { initWallet } = useWallet();
  const navigate = useNavigate();

  const validateMnemonic = (phrase) => {
    const words = phrase.trim().toLowerCase().split(/\s+/);
    // Basic validation: 12 words
    const isValidLength = words.length === 12;
    // Simple check for alphabetic characters
    const hasValidWords = words.every(word => /^[a-z]+$/i.test(word));

    setValidWords(words);
    setIsValid(isValidLength && hasValidWords);
    return isValidLength && hasValidWords;
  };

  const handlePhraseChange = (e) => {
    const newPhrase = e.target.value;
    setMnemonic(newPhrase);
    setError('');
    validateMnemonic(newPhrase);
  };

  const handleClearPhrase = () => {
    setMnemonic('');
    setError('');
    setValidWords([]);
    setIsValid(false);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setMnemonic(text);
      validateMnemonic(text);
    } catch (err) {
      console.error('Failed to paste:', err);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateMnemonic(mnemonic)) {
      setError('Please enter all 12 words of your recovery phrase');
      return;
    }

    setLoading(true);
    setImportStep('Validating recovery phrase...');

    try {
      setImportStep('Initializing wallets...');
      await initWallet(mnemonic);

      setImportStep('Setting up PIN...');
      navigate('/set-pin');
    } catch (error) {
      console.error('Wallet import error:', error);
      setError(error.message || 'Failed to import wallet. Please check your recovery phrase.');
    } finally {
      setLoading(false);
      setImportStep('');
    }
  };

  return (
    <div className="page-container">
      <div className="aurora-bg">
        <div className="aurora-blob aurora-1" />
        <div className="aurora-blob aurora-2" />
        <div className="grid-overlay" />
      </div>

      <motion.div
        className="content-wrapper"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-nav">
          <button className="nav-back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft />
          </button>
          <h1 className="page-title">Import Wallet</h1>
        </div>

        <p className="page-subtitle">
          Enter your 12-word recovery phrase to restore your wallet.
        </p>

        <div className="glass-card">
          <div className="warning-banner">
            <FiAlertTriangle />
            <span>Ensure no one is looking. Never share this phrase.</span>
          </div>

          <form className="import-form" onSubmit={handleImport}>
            <div className="input-group">
              <div className="input-header">
                <label>Recovery Phrase</label>
                <div className="input-actions">
                  <button type="button" onClick={handlePaste} className="text-action-btn">
                    <FiUpload /> Paste
                  </button>
                  <button type="button" onClick={() => setShowPhrase(!showPhrase)} className="text-action-btn">
                    {showPhrase ? <FiEyeOff /> : <FiEye />} {showPhrase ? 'Hide' : 'Show'}
                  </button>
                  {mnemonic && (
                    <button type="button" onClick={handleClearPhrase} className="text-action-btn danger">
                      <FiX /> Clear
                    </button>
                  )}
                </div>
              </div>

              <div className={`textarea-wrapper ${isValid ? 'valid' : ''} ${error ? 'error' : ''}`}>
                <textarea
                  value={mnemonic}
                  onChange={handlePhraseChange}
                  placeholder="apple banana cherry..."
                  rows={4}
                  className="glass-textarea"
                  type={showPhrase ? "text" : "password"}
                  disabled={loading}
                  spellCheck={false}
                />
                <div className="textarea-footer">
                  <span className={`count-badge ${validWords.length === 12 ? 'success' : ''}`}>
                    {validWords.length}/12 words
                  </span>
                </div>
              </div>
            </div>

            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                <FiAlertTriangle /> {error}
              </motion.div>
            )}

            <button
              className="crystal-button btn-primary"
              type="submit"
              disabled={loading || !isValid}
              style={{ opacity: (loading || !isValid) ? 0.5 : 1 }}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  <span>{importStep || 'Processing...'}</span>
                </>
              ) : (
                <>
                  <span>Import Wallet</span>
                  <FiCheck />
                </>
              )}
              <div className="btn-shine" />
            </button>
          </form>
        </div>

        <div className="security-tips">
          <h3><FiShield /> Security Tips</h3>
          <ul>
            <li>Ensure you are on the correct website.</li>
            <li>We will never ask for your phrase via DM or email.</li>
          </ul>
        </div>

      </motion.div>
    </div>
  );
};

export default ImportWalletPage;
